import type { ComboboxData } from "@mantine/core";
import { Box, Group, Select, Text } from "@mantine/core";
import { IconExclamationCircle } from "@tabler/icons-react";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import type { Veraeusserungsform } from "../../../types/api.types";
import { OrangeCircle } from "../../OrangeCircle/OrangeCircle";
import {
  PeriodBasedTable,
  TableCellErrorText
} from "../PeriodBasedTable/PeriodBasedTable";
import { EMPTY_DATE_PLACEHOLDER } from "../PeriodBasedTable/PeriodBasedTable.constants";
import type {
  PeriodBasedRow,
  ValueCellProps,
  ValueEditProps
} from "../PeriodBasedTable/PeriodBasedTable.types";
import { MarketingTypeDeleteModalContent } from "./MarketingTypeDeleteModalContent";
import { MarketingTypeEditModalContent } from "./MarketingTypeEditModalContent";
import { MARKETING_TYPE_OPTIONS } from "./MarketingTypeTable.constants";

export type MarketingValueField = {
  veraeusserungsform: string;
};
export type MarketingTypeRow = PeriodBasedRow<MarketingValueField>;

function getWarningColumn(
  isMarktpraemie: boolean
): MRT_ColumnDef<PeriodBasedRow<MarketingValueField>> {
  const warningText = isMarktpraemie
    ? "Förderung/Anzulegender\nWert nicht aktiv"
    : "Förderung/Einspeise-\nvergütung nicht aktiv";

  return {
    header: "",
    accessorKey: "warning",
    size: 190,
    enableSorting: false,
    Edit: ({ row, table }) => {
      const isCreatingRow = table.getState().creatingRow?.id === row.id;
      const isEditingRow = table.getState().editingRow?.id === row.id;

      const currentValue =
        isCreatingRow || isEditingRow
          ? (row._valuesCache.veraeusserungsform ??
            row.original.veraeusserungsform)
          : row.original.veraeusserungsform;

      if (currentValue === "other_direct_marketing") {
        return (
          <Group gap={4} wrap="nowrap">
            <IconExclamationCircle
              color="var(--mantine-color-yellow-6)"
              size={16}
            />
            <Text fz="s" style={{ whiteSpace: "pre-line" }}>
              {warningText}
            </Text>
          </Group>
        );
      }
      return null;
    },
    Cell: ({ row }) => {
      if (row.original.veraeusserungsform === "other_direct_marketing") {
        return (
          <Group gap={4} wrap="nowrap">
            <IconExclamationCircle
              color="var(--mantine-color-yellow-6)"
              size={16}
            />
            <Text c="dimmed" fz="xs" style={{ whiteSpace: "pre-line" }}>
              {warningText}
            </Text>
          </Group>
        );
      }
      return null;
    }
  };
}

export type MarketingTypeCellProps = ValueCellProps<MarketingValueField> & {
  highlightMissing?: boolean;
};

export const MarketingTypeCell = ({
  row,
  error,
  highlightMissing = false
}: MarketingTypeCellProps) => {
  if (row.veraeusserungsform) {
    const option = MARKETING_TYPE_OPTIONS.find(
      (opt) => opt.value === row.veraeusserungsform
    );
    const label = option?.label || row.veraeusserungsform;

    return (
      <Box>
        {label}
        <TableCellErrorText error={error} />
      </Box>
    );
  }

  return (
    <Box>
      <Group gap="xs" wrap="nowrap">
        {!error && highlightMissing && <OrangeCircle />}
        <Text span>{EMPTY_DATE_PLACEHOLDER}</Text>
      </Group>
      <TableCellErrorText error={error} />
    </Box>
  );
};

export interface MarketingTypeTableProps {
  periodBasedData: MarketingTypeRow[];
  onChange: (updatedRows: MarketingTypeRow[]) => void;
  commissioningDate?: string;
  isMarktpraemie?: boolean;
  generatorId?: number;
  variantId?: number;
  readOnly?: boolean;
  highlightMissing?: boolean;
  externalErrors?: Record<
    string,
    {
      first_date?: string;
      last_date?: string;
      veraeusserungsform?: string;
    }
  >;
}

export function MarketingTypeEdit({
  value,
  onChange,
  error,
  filteredOptions
}: ValueEditProps<MarketingValueField> & {
  filteredOptions: ComboboxData;
}) {
  const [localValue, setLocalValue] = useState<string>((value as string) ?? "");

  return (
    <Select
      data={filteredOptions}
      error={error}
      placeholder="Wählen Sie eine Option"
      value={localValue}
      onChange={(newValue) => {
        setLocalValue(newValue ?? "");
        onChange(newValue || undefined);
      }}
    />
  );
}

export function MarketingTypeTable({
  externalErrors,
  generatorId,
  highlightMissing = false,
  isMarktpraemie = false,
  onChange,
  periodBasedData,
  readOnly = false,
  variantId
}: MarketingTypeTableProps) {
  const filteredOptions = useMemo(() => {
    if (isMarktpraemie) {
      return MARKETING_TYPE_OPTIONS.filter(
        (option) => option.value !== "feste_einspeiseverguetung"
      );
    }
    return MARKETING_TYPE_OPTIONS;
  }, [isMarktpraemie]);

  const createRow = useCallback(
    (
      values: Record<string, string | number | null>
    ): PeriodBasedRow<MarketingValueField> => {
      return {
        first_date: (values.first_date as string) || "",
        last_date: (values.last_date as string | null) || null,
        veraeusserungsform: (values.veraeusserungsform as string) || ""
      };
    },
    []
  );

  const additionalColumns = useMemo(() => {
    const hasWarning = periodBasedData.some(
      (row) => row.veraeusserungsform === "other_direct_marketing"
    );

    if (hasWarning) {
      return [getWarningColumn(isMarktpraemie)];
    }
    return [];
  }, [isMarktpraemie, periodBasedData]);

  return (
    <PeriodBasedTable
      additionalColumns={additionalColumns}
      createRow={createRow}
      DeleteModalBody={(row) => (
        <MarketingTypeDeleteModalContent
          generatorId={generatorId}
          row={row}
          variantId={variantId}
        />
      )}
      deleteModalDataTypeName="die Veräußerungsform"
      EditModalBody={(row, newValues) => {
        const oldVeraeusserungsform =
          row.veraeusserungsform as Veraeusserungsform;
        const newVeraeusserungsform =
          newValues?.veraeusserungsform as Veraeusserungsform;
        return (
          <MarketingTypeEditModalContent
            newVeraeusserungsform={newVeraeusserungsform}
            oldVeraeusserungsform={oldVeraeusserungsform}
          />
        );
      }}
      editModalDataTypeName="die Veräußerungsform"
      externalErrors={externalErrors}
      periodBasedData={periodBasedData}
      readOnly={readOnly}
      valueColumn={{
        header: "Veräußerungsform",
        fieldName: "veraeusserungsform",
        CellComponent: (props) => (
          <MarketingTypeCell
            {...props}
            highlightMissing={highlightMissing && !readOnly}
          />
        ),
        EditComponent: (props) => (
          <MarketingTypeEdit filteredOptions={filteredOptions} {...props} />
        )
      }}
      onChange={onChange}
    />
  );
}
